import { Injectable } from '@angular/core';
import { ContentService } from '@blk/cwp-ng-framework';
import { InjectConfig, SharedConfigKey } from '@ddt-web/config-layer';
import { LocalStorageKey } from '@ddt-web/generic-utils';
import { PartnerQueryParamKey } from '@ddt-web/url';
import * as get from 'lodash/get';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { PartnerConstants } from '../constants/partner.constants';
import { UserProfile } from '../models/user-profile';
import { UserDataService } from '../services/user-data.service';

@Injectable({
	providedIn: 'root'
})
export class PartnerUserProfileService {
	@InjectConfig(SharedConfigKey.ENABLE_EXTSSO_CHECK) private readonly isExtSsoCheckEnabled: boolean = false;
	private partnerViewFirmNameList: string[];

	constructor(private userDataService: UserDataService, private contentService: ContentService) {
		// TODO Waris: this override will be removed in Q2
		const overridePartnerList = this.contentService.get('OVERRIDE_SHOW_PARTNERS_VIEW_LIST', '');
		if (overridePartnerList) {
			this.partnerViewFirmNameList = overridePartnerList.replace(/\s/g, '').split(',');
		} else {
			this.partnerViewFirmNameList = PartnerConstants.SHOW_PARTNERS_VIEW;
		}
	}

	/**
	 * return the partner name
	 *
	 * @returns {Observable<string>}
	 * @memberof UserDataService
	 */
	public getPartner(): Observable<string> {
		return this.userDataService.getUserProfile().pipe(
			map(userProfile => {
				let ssoInitiatedFrom = get(userProfile, PartnerConstants.SSO_TOKEN_PATH, localStorage.getItem(LocalStorageKey.SsoInitiatedFrom));
				if (this.isExtSsoCheckEnabled) {
					const isExternalSso = get(userProfile, PartnerConstants.EXT_SSO_TOKEN_PATH, localStorage.getItem(LocalStorageKey.ExtSso)) === 'true';
					ssoInitiatedFrom = isExternalSso ? ssoInitiatedFrom : null;
				}
				return ssoInitiatedFrom;
			})
		);
	}

	/**
	 * Returns a boolean observable that represents whether the current user has
	 * a sso session initiated from an advisor centre partner
	 */

	public isPartnerUser(): Observable<boolean> {
		return this.userDataService.getUserProfile().pipe(
			map((userData: UserProfile) => {
				const saleFirmName: string = this.formatPartnerSalesFirmName(userData.salesFirmName ?? '');
				const testPartnerFirm: string = this.getTestPartnerFirmParam(userData);

				let isPartnerUser = !!get(userData, PartnerConstants.SSO_TOKEN_PATH, localStorage.getItem(LocalStorageKey.SsoInitiatedFrom));
				if (this.isExtSsoCheckEnabled) {
					const isExternalSso = get(userData, PartnerConstants.EXT_SSO_TOKEN_PATH, localStorage.getItem(LocalStorageKey.ExtSso)) === 'true';
					isPartnerUser = isExternalSso && isPartnerUser;
				}
				return isPartnerUser
					|| this.partnerViewFirmNameList.includes(testPartnerFirm)
					|| this.partnerViewFirmNameList.includes(saleFirmName);
			})
		);
	}

	/**
	 * Returns a boolean observable that represents whether the current user has
	 * a sso session initiated from an "activated" partner
	 */

	public isActivatedPartnerUser(): Observable<boolean> {
		return this.getPartner().pipe(
			map((partnerName) => !!partnerName && PartnerConstants.ACTIVATED_PARTNERS.includes(partnerName))
		);
	}

	public getSalesFirmName(): Observable<string> {
		return this.userDataService.getUserProfile().pipe(
			map(userData => this.formatPartnerSalesFirmName(userData.salesFirmName ?? ''))
		);
	}

	public getTestPartnerFirm(): Observable<string> {
		return this.userDataService.getUserProfile().pipe(map(this.getTestPartnerFirmParam));
	}

	public getCrmFirmId(): Observable<string> {
		return this.userDataService.getUserProfile().pipe(map(userProfile =>
			userProfile?.['crmFirmId']
			?? userProfile?.userAttributes?.['crmFirmId']
			?? userProfile?.userExtendedAttributes?.['crmFirmId']
			?? userProfile?.['firmName']
		));
	}

	private formatPartnerSalesFirmName(salesFirmName: string): string {
		return salesFirmName.toLowerCase().replace(/[^\w]+/g, '_');
	}

	private getTestPartnerFirmParam(userProfile: UserProfile): string {
		return (get(userProfile, 'usAuthAccessLevel') === 'INTERNAL' || userProfile.internalUser)
			? new URL(window.location.href).searchParams.get(PartnerQueryParamKey.TEST_PARTNER_FIRM) : '';
	}
}
